import { ENTER_NAME, ESC_NAME, NUMPAD_ENTER_NAME } from '@anm/constants/keyCodes';
import useDiffSave from '@anm/hooks/useDiffSave';
import { UserFormRow } from '@anm/styles/helpers';
import { Config as FinalFormConfig } from 'final-form';
import { FC, KeyboardEvent } from 'react';
import { Field, Form, FormRenderProps } from 'react-final-form';

import { UploadAvatarData } from '../../AvatarUpload';
import FormHelperText from '../../form/FormHelperText';
import UserAvatarInput from '../../form/UserAvatarInput';

import { ButtonLoaderWrapper, TeamFormSection, TeamName, WrapperTeamForm } from './Wrapper';

type HOCs = FinalFormConfig<TeamFormFields>;
export type TeamFormProps = {
  onAutoSave: (fields: TeamFormFields) => void;
  isPending?: boolean;
  submitError?: string | null;
};

export type TeamFormFields = {
  avatar?: UploadAvatarData | string;
  displayName?: string;
};

const renderForm = ({ submitError, onAutoSave, isPending }: TeamFormProps) => ({
  values,
  form
}: FormRenderProps<TeamFormFields>) => {
  const { dirty, valid } = form.getState();
  const submit = () => {
    valid && onAutoSave(values);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ([ENTER_NAME, NUMPAD_ENTER_NAME].includes(event.code)) {
      event.preventDefault();
      submit();
    }
    if (event.code === ESC_NAME) form.reset();
  };

  return (
    <WrapperTeamForm onSubmit={event => event.preventDefault()} onKeyDown={handleKeyDown}>
      <TeamFormSection>
        {submitError && (
          <UserFormRow>
            <FormHelperText variant="belowField">{submitError}</FormHelperText>
          </UserFormRow>
        )}

        <UserFormRow>
          <Field name="avatar" component={UserAvatarInput} variant="inline" />
        </UserFormRow>
        <TeamName name="displayName" label="Company Name" helperTextVariant="belowField" maxLength={60} />

        <ButtonLoaderWrapper
          size="large_long_static"
          color="blue"
          variant={dirty ? 'blue-heavy-border' : 'gray-border'}
          disabled={!dirty}
          isPending={isPending}
          onClick={submit}
        >
          Save Changes
        </ButtonLoaderWrapper>
      </TeamFormSection>
    </WrapperTeamForm>
  );
};

const TeamForm: FC<HOCs & TeamFormProps> = ({ initialValues, onAutoSave, ...otherProps }) => {
  const save = useDiffSave<TeamFormFields>({ initValues: initialValues, onSave: onAutoSave });

  return (
    <Form initialValues={initialValues} render={renderForm({ onAutoSave: save, ...otherProps })} {...otherProps} />
  );
};

export default TeamForm;
